import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/lib/api-client';

export type UserRoleRef = { id: string; name: string };

export type UserSummary = {
  id: string;
  email: string;
  displayName: string;
  isSuperAdmin: boolean;
  isActive: boolean;
  roles: UserRoleRef[];
  lastLoginAt: string | null;
  createdAt: string;
};

export type UsersListResponse = { items: UserSummary[]; total: number };

const LIST_KEY = ['users'] as const;

export function useUsers() {
  return useQuery({
    queryKey: LIST_KEY,
    queryFn: async (): Promise<UsersListResponse> => {
      const res = await apiClient.get<UsersListResponse>('/users');
      return res.data;
    },
  });
}

export type UserCreateBody = {
  email: string;
  displayName: string;
  password: string;
  isSuperAdmin: boolean;
  /** Optional initial role set (role ids). Omit or [] for no roles. */
  roleIds?: string[];
};

export function useCreateUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: UserCreateBody): Promise<UserSummary> => {
      const res = await apiClient.post<UserSummary>('/users', body);
      return res.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: LIST_KEY }),
  });
}

export type UserUpdateBody = {
  email?: string;
  displayName?: string;
  isSuperAdmin?: boolean;
  isActive?: boolean;
  /** Full replacement of the user's role set. */
  roleIds?: string[];
};

export function useUpdateUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: { id: string; body: UserUpdateBody }): Promise<UserSummary> => {
      const res = await apiClient.patch<UserSummary>(`/users/${args.id}`, args.body);
      return res.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: LIST_KEY }),
  });
}

export function useResetUserPassword() {
  return useMutation({
    mutationFn: async (args: { id: string; password: string }): Promise<void> => {
      await apiClient.post(`/users/${args.id}/password`, { password: args.password });
    },
  });
}
